import { useLazyQuery } from "@apollo/client";
import { IonContent, IonInfiniteScroll, IonInfiniteScrollContent } from "@ionic/react";
import { isToday, isYesterday } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import ClientAvatar from "@components/ClientAvatar";
import DateStamp from "@components/dates/DateStamp";
import ExpertAvatar from "@components/ExpertAvatar";
import InputSelect from "@components/fieldsNative/InputSelect";
import MessageForm from "@components/forms/MessageForm";
import HandleQuery from "@components/HandleQuery";
import Tag from "@components/labels/Tag";
import Message from "@components/messaging/Message";
import { MessageSearch, ResourceData, WorksheetData } from "@components/messaging/Messages";
import styles from "@components/messaging/Messages.module.css";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import InternalLink from "@components/typography/InternalLink";
import useSubscribeToMessageTypers from "@hooks/subscriptions/useSubscribeToMessageTypers";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMe from "@hooks/useMe";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import { Opener } from "@hooks/useOpener";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  ChatFragment,
  ChatMessagesMarkReadDocument,
  ChatMessagesMarkReadMutation,
  ChatMessagesMarkReadMutationVariables,
  GetChatMessagesDocument,
  GetChatMessagesQuery,
  GetChatMessagesQueryVariables,
  GetJourneyForMessagesQuery,
  MessageFragment,
  UserTypeEnum
} from "@typing/Generated";
import { Colors } from "@utils/colorUtils";
import { expertRolesWithoutBackups, getChatUsers, groupByTimestampDay, modelWithUniqueIds } from "@utils/modelUtils";
import { isMobileWeb, isNativeApp } from "@utils/platformUtils";
import { journeyRoute } from "@utils/routeUtils";
import { capitalizeFirstLetter } from "@utils/stringUtils";

type Props = {
  chat: ChatFragment;
  clearChat?: () => void;
  editingChat: boolean;
  expandedOpener?: Opener;
  isFab?: boolean;
  journey: GetJourneyForMessagesQuery["journey"];
  messageSearch: MessageSearch;
  onChatClientsChange: (ids: string[]) => void;
  resourceData: ResourceData | undefined;
  setMessageSearch: (messageSearch: MessageSearch) => void;
  setResourceData: (resourceData: ResourceData | undefined) => void;
  setWorksheetData: (worksheetData: WorksheetData | undefined) => void;
  worksheetData: WorksheetData | undefined;
};

interface MessageRefs {
  [key: string]: HTMLDivElement | null;
}

const Chat = ({
  chat,
  clearChat,
  editingChat = false,
  expandedOpener,
  isFab = false,
  journey,
  messageSearch,
  onChatClientsChange,
  resourceData,
  setMessageSearch,
  setResourceData,
  setWorksheetData,
  worksheetData
}: Props) => {
  const intl = useIntl();
  const me = useMe();
  const myClient = useMyClient();
  const myExpert = useMyExpert();

  const chatUsers = getChatUsers(me, chat, journey);

  const [shouldAnimate, setShouldAnimate] = useState(false);

  const [getChatMessages, getChatMessagesQuery] = useLazyQuery<GetChatMessagesQuery, GetChatMessagesQueryVariables>(
    GetChatMessagesDocument
  );

  useEffect(() => {
    getChatMessages({
      variables: { id: chat.id }
    });
  }, [chat.id, getChatMessages]);

  async function searchNext(event: CustomEvent<void>) {
    await getChatMessagesQuery
      .fetchMore({
        variables: { cursor: getChatMessagesQuery.data?.chatMessages.pageInfo?.endCursor ?? "", id: chat.id }
      })
      .then(() => (event.target as HTMLIonInfiniteScrollElement).complete());
  }

  const messages = useNodesFromConnection(getChatMessagesQuery.data?.chatMessages);

  const messageTypers = useSubscribeToMessageTypers({ chatId: chat.id });

  const messageContentRef = useRef<HTMLIonContentElement>(null);
  const [messageRefs, setMessageRefs] = useState<MessageRefs>({});

  const registerMessageRef = (messageId: string, ref: HTMLDivElement | null) => {
    setMessageRefs(prevMessageRefs => ({
      ...prevMessageRefs,
      [messageId]: ref
    }));
  };

  const [chatMessagesMarkRead] = useMutationWithValidation<
    ChatMessagesMarkReadMutation,
    ChatMessagesMarkReadMutationVariables
  >(ChatMessagesMarkReadDocument, "messages");

  useEffect(() => {
    if (!myExpert || journey.expertRoles?.some(role => role.expert.id === myExpert?.id)) {
      chatMessagesMarkRead({
        id: chat.id
      });
    }
  }, [chat.id, chatMessagesMarkRead, journey.expertRoles, myExpert]);

  useEffect(() => {
    if (messageContentRef.current) {
      messageContentRef.current.scrollToBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (messageSearch.found === true && messageSearch.messageId && messageRefs[messageSearch.messageId]) {
      const messageId = messageSearch.messageId;
      setTimeout(() => {
        messageRefs[messageId]?.scrollIntoView({ behavior: "auto", block: "center" });
      }, 500);
      setMessageSearch({ ...messageSearch, found: null });
    }
  }, [messageRefs, messageSearch, messageSearch.found, messageSearch.messageId, setMessageSearch]);

  const sortedMessagesByDay = useMemo(
    () => groupByTimestampDay<MessageFragment>(messages, message => message.createdAt, intl, true),
    [intl, messages]
  );

  const getUserColor = (userId: string) => {
    const user = chatUsers.find(user => user.id === userId);

    if (user && "firstName" in user?.avatarProps) {
      return user?.avatarProps.color;
    }
  };

  const myExpertRole = journey.expertRoles?.find(role => role.expert.id === myExpert?.id);
  const messagesOpener = useGlobalOpener(OpenerTypeEnum.MESSAGES);

  const handleMessageCreate = () => {
    setShouldAnimate(true);
    messageContentRef.current?.scrollToBottom();

    setTimeout(() => {
      setShouldAnimate(false);
    }, 2000);
  };

  useEffect(() => {
    messageContentRef.current?.scrollToBottom();
  }, [messages.length]);

  const searchForMessage = useCallback(async () => {
    let message: MessageFragment | null =
      getChatMessagesQuery.data?.chatMessages.edges?.find(edge => edge?.node?.id === messageSearch.messageId)?.node ??
      null;

    let cursor = getChatMessagesQuery.data?.chatMessages.pageInfo?.endCursor ?? "";

    while (message === null) {
      const res = await getChatMessagesQuery.fetchMore({
        variables: { cursor, id: chat.id }
      });

      if (res.data?.chatMessages.edges) {
        cursor = res.data.chatMessages.pageInfo?.endCursor ?? "";
        message = res.data?.chatMessages.edges?.find(edge => edge?.node?.id === messageSearch.messageId)?.node ?? null;
      }
    }

    if (message) {
      setMessageSearch({ ...messageSearch, found: true });
    }
  }, [getChatMessagesQuery, messageSearch, setMessageSearch, chat.id]);

  useEffect(() => {
    if (messageSearch.found === false && messageSearch.messageId) {
      searchForMessage();
    }
  }, [
    getChatMessagesQuery.data?.chatMessages.pageInfo?.endCursor,
    messageSearch.found,
    messageSearch.messageId,
    searchForMessage
  ]);

  useEffect(() => {
    if (!getChatMessagesQuery.loading) setTimeout(() => messageContentRef.current?.scrollToBottom(), 300);
  }, [chat.id, getChatMessagesQuery.loading]);

  const colorOrder: Colors[] = ["secondary-100", "secondary", "primary-300", "primary"];

  const handleChatClientsChange = (values: string[]) => {
    onChatClientsChange(values.filter(v => journey.clientRoles.some(cr => cr.client.id === v)));
  };

  const expertOptions =
    expertRolesWithoutBackups(journey.expertRoles)
      ?.filter(er => er.expert.id !== me?.id)
      .map(er => ({
        image: <ExpertAvatar expert={er.expert} />,
        isFixed: true,
        label: er.expert.firstName,
        value: er.expert.id
      })) ?? [];

  const clientOptions = journey.clientRoles
    .filter(role => role.client.id !== myClient?.id)
    .map((cr, index) => ({
      image: (
        <ClientAvatar
          clientId={cr.client.id}
          color={colorOrder[index % colorOrder.length]}
          firstName={cr.client.firstName}
          journeyId={journey.id}
          lastName={cr.client.lastName}
        />
      ),
      isFixed: cr.client.id === journey.owner.id,
      label: cr.client.firstName,
      value: cr.client.id
    }));

  const newChatIds = Array.from(
    new Set([...expertOptions.map(option => option.value), ...chat.clients.map(client => client.id), journey.owner.id])
  );

  return (
    <div
      className={`${isFab ? styles.chatFab : styles.chat} ${
        isNativeApp() || (isMobileWeb() && !isFab) ? styles.menuSpacing : ""
      }`}
    >
      {editingChat ? (
        <div className={styles.editingChatHeader}>
          {clearChat && (
            <div className={styles.chatNewMessageHeader}>
              <div className={styles.chatNewMessage}>
                <div className={styles.chatGoBack}>
                  <ActionIconButton
                    ariaLabelKey="accessibility.ariaLabels.goBack"
                    icon="chevronLeft"
                    onClick={clearChat}
                    size="medium"
                  />
                </div>
                <Heading2 bold={false}>
                  <FormattedMessage id="pages.messages.newMessage" />
                </Heading2>
              </div>
              <div className={styles.close}>
                <ActionIconButton
                  ariaLabelKey="accessibility.ariaLabels.close"
                  color="gray-500"
                  icon="times"
                  onClick={messagesOpener.close}
                  size="medium"
                />
              </div>
            </div>
          )}
          <InputSelect
            noOptionsLabel={
              intl.formatMessage(
                { id: "pages.messages.noClients" },
                {
                  profile: (
                    <InternalLink
                      to={journeyRoute({
                        journeyId: journey.id,
                        linkAfterJourney: "/care-profile",
                        myClient
                      })}
                    >
                      <FormattedMessage id="pages.messages.profile" />
                    </InternalLink>
                  )
                }
              ) as string
            }
            onChange={handleChatClientsChange}
            options={[...expertOptions, ...clientOptions]}
            placeholder={intl.formatMessage({ id: "pages.messages.typeName" })}
            values={newChatIds}
          />
        </div>
      ) : (
        <div className={styles.chatNewMessageHeader}>
          <div className={styles.chatHeader}>
            <div className={styles.controlChatUsers}>
              {clearChat && (
                <div className={styles.chatGoBack}>
                  <ActionIconButton
                    ariaLabelKey="accessibility.ariaLabels.goBack"
                    icon="chevronLeft"
                    onClick={clearChat}
                    size="medium"
                  />
                </div>
              )}
              <div className={styles.controlsAvatars}>
                {myClient ? (
                  <ClientAvatar
                    clientId={myClient.id}
                    color="primary-500"
                    firstName={myClient.firstName}
                    journeyId={journey.id}
                    lastName={myClient.lastName}
                  />
                ) : myExpertRole ? (
                  <ExpertAvatar expert={myExpertRole.expert} />
                ) : (
                  <div className={styles.fakeAvatar} />
                )}
                {chatUsers.map((user, index) => (
                  <div className={styles.controlsAvatarContainer} key={user.id}>
                    <div
                      className={styles.controlsAvatar}
                      style={{
                        left:
                          myExpert && !myExpertRole
                            ? (chatUsers.length - index - 1) * 22
                            : (chatUsers.length - index) * 22,
                        zIndex: chatUsers.length + 1 - index
                      }}
                    >
                      {user.userType === UserTypeEnum.EXPERT && "expert" in user.avatarProps ? (
                        <ExpertAvatar {...user.avatarProps} />
                      ) : "firstName" in user.avatarProps ? (
                        <ClientAvatar {...user.avatarProps} clientId={user.id} journeyId={journey.id} />
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.hideMobile}>
                <Heading2 bold={!isFab} color="gray-500">
                  {(myExpert && !myExpertRole
                    ? []
                    : [capitalizeFirstLetter(intl.formatMessage({ id: "dictionary.you" }))]
                  )
                    .concat(chatUsers.map(user => user.name))
                    .join(", ")}
                </Heading2>
              </div>

              <div className={styles.showMobile + " " + styles.oneLine}>
                <BodyCopy bold color="gray-500">
                  {[capitalizeFirstLetter(intl.formatMessage({ id: "dictionary.you" }))]
                    .concat(chatUsers.map(user => user.name))
                    .join(", ")}
                </BodyCopy>
              </div>
            </div>
          </div>
          {clearChat && (
            <div className={styles.closeFab}>
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.close"
                color="gray-500"
                icon={expandedOpener?.isOpen ? "compress" : "expand"}
                onClick={expandedOpener?.toggle}
                size="medium"
              />
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.close"
                color="gray-500"
                icon="times"
                onClick={messagesOpener.close}
                size="medium"
              />
            </div>
          )}
        </div>
      )}

      <HandleQuery query={getChatMessagesQuery}>
        {_data => (
          <div className={styles.scroll}>
            <IonContent ref={messageContentRef} scrollY>
              <IonInfiniteScroll
                disabled={getChatMessagesQuery.data?.chatMessages.pageInfo?.hasNextPage === false}
                onIonInfinite={searchNext}
                position="top"
                threshold="100px"
              >
                <IonInfiniteScrollContent />
              </IonInfiniteScroll>
              <AnimatePresence initial={false}>
                <div className={styles.chatMessages}>
                  {sortedMessagesByDay.map(group => (
                    <div key={`${group.year}-${group.month}-${group.date}`}>
                      <div className={styles.chatMessagesDate}>
                        <div className={styles.chatLine} />
                        <Tag color="primary-500">
                          <div className={styles.noWrap}>
                            {isToday(new Date(group.year, group.month, group.date)) ? (
                              <FormattedMessage id="dictionary.today" />
                            ) : isYesterday(new Date(group.year, group.month, group.date)) ? (
                              <FormattedMessage id="dictionary.yesterday" />
                            ) : (
                              <DateStamp
                                showYear={group.year !== new Date().getFullYear()}
                                value={new Date(group.year, group.month, group.date)}
                              />
                            )}
                          </div>
                        </Tag>
                        <div className={styles.chatLine} />
                      </div>
                      {modelWithUniqueIds(group.models).map((message, index) => {
                        const hideAvatar = index > 0 && message.author.id === group.models[index - 1].author.id;

                        return (
                          <motion.div
                            animate={{ opacity: 1, scale: 1 }}
                            initial={shouldAnimate ? { opacity: 0, scale: 0.8 } : false}
                            key={message.id}
                            ref={ref => registerMessageRef(message.id, ref)}
                            transition={{ opacity: { duration: 0.2 } }}
                          >
                            <Message
                              authorColor={getUserColor(message.author.id)}
                              authorOfPreviousMessage={hideAvatar}
                              journeyId={journey.id}
                              message={message}
                              messageSearch={messageSearch}
                            />
                          </motion.div>
                        );
                      })}
                    </div>
                  ))}
                  <div className={styles.anchor}></div>
                </div>
              </AnimatePresence>
            </IonContent>
          </div>
        )}
      </HandleQuery>

      <div className={styles.chatForm}>
        <MessageForm
          chat={chat}
          contentRef={messageContentRef}
          journey={journey}
          messagingDisabled={!journey.hasExpertAccess}
          onMessageCreate={handleMessageCreate}
          resourceData={resourceData}
          setResourceData={setResourceData}
          setWorksheetData={setWorksheetData}
          worksheetData={worksheetData}
        />
        {messageTypers.length > 0 && (
          <div className={styles.typingBox}>
            <Icon icon="ellipsis" />
            <BodyCopy color="gray-500">
              {messageTypers
                .map(typer => typer.user?.firstName)
                .join(intl.formatMessage({ id: "models.messages.and" }))}
              <FormattedMessage id="models.messages.areTyping" values={{ count: messageTypers.length }} />
            </BodyCopy>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;
